package dev.danko.roblox_craft_ai.uikit.components

import dev.danko.roblox_craft_ai.uikit.i18n.strings
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.icons.fa.FaIcon
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import org.jetbrains.compose.web.css.*

data class SelectOption<T>(
    val value: T,
    val label: String,
    val description: String? = null,
    val icon: (@Composable () -> Unit)? = null
)

@Composable
fun <T> AppSelect(
    options: List<SelectOption<T>>,
    selected: T?,
    onSelect: (T) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    placeholder: String = strings().common.selectPlaceholder,
    disabled: Boolean = false
) {
    val theme = LocalAppTheme.current
    val palette = theme.colors
    var isOpen by remember { mutableStateOf(false) }

    val selectedOption = options.firstOrNull { it.value == selected }

    Column(modifier = Modifier.fillMaxWidth().then(modifier)) {
        if (!label.isNullOrBlank()) {
            SpanText(
                text = label,
                modifier = Modifier
                    .fontSize(0.85.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .color(palette.textPrimary)
                    .margin(bottom = 0.35.cssRem)
            )
        }

        Box(modifier = Modifier.fillMaxWidth().position(Position.Relative)) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .backgroundColor(palette.surfaceElevated)
                    .borderRadius(theme.radii.md)
                    .border(
                        width = 1.px,
                        style = LineStyle.Solid,
                        color = if (isOpen) palette.accent else palette.border
                    )
                    .boxShadow(
                        0.px,
                        0.px,
                        0.px,
                        if (isOpen) 2.px else 0.px,
                        if (isOpen) palette.accent.toRgb().copyf(alpha = 0.35f) else Color.rgba(0, 0, 0, 0f)
                    )
                    .padding(leftRight = 0.75.cssRem, topBottom = 0.55.cssRem)
                    .cursor(if (disabled) Cursor.NotAllowed else Cursor.Pointer)
                    .opacity(if (disabled) 0.5 else 1.0)
                    .onClick {
                        if (!disabled) isOpen = !isOpen
                    }
                    .transition(Transition.of("border-color", 150.ms), Transition.of("box-shadow", 150.ms)),
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (selectedOption?.icon != null) {
                    Box(modifier = Modifier.margin(right = 0.5.cssRem)) {
                        selectedOption.icon.invoke()
                    }
                }

                SpanText(
                    text = selectedOption?.label ?: placeholder,
                    modifier = Modifier
                        .weight(1f)
                        .fontSize(0.95.cssRem)
                        .color(if (selectedOption != null) palette.textPrimary else palette.textSecondary)
                )

                FaIcon(
                    name = "chevron-down",
                    modifier = Modifier
                        .fontSize(0.75.cssRem)
                        .color(palette.textSecondary)
                        .rotate(if (isOpen) 180.deg else 0.deg)
                        .transition(Transition.of("transform", 200.ms))
                )
            }

            if (isOpen && !disabled) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .position(Position.Absolute)
                        .top(100.percent)
                        .margin(top = 4.px)
                        .backgroundColor(palette.surface)
                        .border(width = 1.px, style = LineStyle.Solid, color = palette.border)
                        .borderRadius(theme.radii.md)
                        .boxShadow(0.px, 4.px, 24.px, color = Color.rgba(0, 0, 0, 0.4f))
                        .zIndex(100)
                        .padding(4.px)
                ) {
                    options.forEach { opt ->
                        val isCurrent = opt.value == selected
                        var isHovered by remember { mutableStateOf(false) }

                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(leftRight = 0.65.cssRem, topBottom = 0.45.cssRem)
                                .borderRadius(theme.radii.sm)
                                .backgroundColor(
                                    when {
                                        isHovered -> palette.surfaceElevated
                                        isCurrent -> palette.surfaceElevated
                                        else -> Color.rgba(0, 0, 0, 0f)
                                    }
                                )
                                .cursor(Cursor.Pointer)
                                .onMouseEnter { isHovered = true }
                                .onMouseLeave { isHovered = false }
                                .onClick {
                                    onSelect(opt.value)
                                    isOpen = false
                                },
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            if (opt.icon != null) {
                                Box(modifier = Modifier.margin(right = 0.5.cssRem)) {
                                    opt.icon.invoke()
                                }
                            }

                            Column(modifier = Modifier.weight(1f)) {
                                SpanText(
                                    text = opt.label,
                                    modifier = Modifier
                                        .fontSize(0.9.cssRem)
                                        .fontWeight(if (isCurrent) FontWeight.Bold else FontWeight.Normal)
                                        .color(if (isCurrent) palette.accent else palette.textPrimary)
                                )
                                if (!opt.description.isNullOrBlank()) {
                                    SpanText(
                                        text = opt.description,
                                        modifier = Modifier
                                            .fontSize(0.75.cssRem)
                                            .color(palette.textSecondary)
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
